import React from 'react';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  subMonths, 
  addMonths,
  startOfYear, 
  endOfYear, 
  subYears,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  parseISO
} from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, X } from 'lucide-react';
import clsx from 'clsx';

export interface DateRange {
  from: string; // ISO date string (YYYY-MM-DD)
  to: string; // ISO date string (YYYY-MM-DD)
}

export type DateRangePreset = 'this-month' | 'last-month' | 'last-3-months' | 'last-6-months' | 'last-9-months' | 'this-year' | 'last-year' | 'custom';

interface TransactionDatePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
  maxDate?: Date; // Maximum selectable date (defaults to today)
}

function formatDateIso(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function getPresetRange(preset: DateRangePreset, maxDate?: Date): DateRange {
  const now = maxDate || new Date();
  const lastMonthEnd = endOfMonth(subMonths(now, 1));
  
  switch (preset) {
    case 'this-month':
      return {
        from: formatDateIso(startOfMonth(now)),
        to: formatDateIso(now),
      };
    case 'last-month':
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      return {
        from: formatDateIso(lastMonthStart),
        to: formatDateIso(lastMonthEnd),
      };
    case 'last-3-months':
      return {
        from: formatDateIso(startOfMonth(subMonths(now, 3))),
        to: formatDateIso(lastMonthEnd),
      };
    case 'last-6-months':
      return {
        from: formatDateIso(startOfMonth(subMonths(now, 6))),
        to: formatDateIso(lastMonthEnd),
      };
    case 'last-9-months':
      return {
        from: formatDateIso(startOfMonth(subMonths(now, 9))),
        to: formatDateIso(lastMonthEnd),
      };
    case 'this-year':
      return {
        from: formatDateIso(startOfYear(now)),
        to: formatDateIso(endOfYear(now)),
      };
    case 'last-year':
      const lastYear = subYears(now, 1);
      return {
        from: formatDateIso(startOfYear(lastYear)),
        to: formatDateIso(endOfYear(lastYear)),
      };
    case 'custom':
      return {
        from: formatDateIso(startOfMonth(subMonths(now, 1))),
        to: formatDateIso(lastMonthEnd),
      };
  }
}

export const DATE_RANGE_PRESET_OPTIONS: Array<{ value: DateRangePreset; label: string }> = [
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'this-year', label: 'This Year' },
  { value: 'last-year', label: 'Last Year' },
];

function formatDateRangeDisplay(from: string, to: string): string {
  try {
    const fromDate = parseISO(from);
    const toDate = parseISO(to);
    
    // Format: "Jan 1 - Dec 31, 2024" or "Jan 1 - Jan 31, 2024" (same year)
    const fromFormatted = format(fromDate, 'MMM d');
    const toFormatted = format(toDate, 'MMM d');
    const year = format(toDate, 'yyyy');
    
    // Check if same year
    const fromYear = format(fromDate, 'yyyy');
    if (fromYear === year) {
      // Same year: "Jan 1 - Dec 31, 2024"
      return `${fromFormatted} - ${toFormatted}, ${year}`;
    } else {
      // Different years: "Dec 1, 2023 - Jan 31, 2024"
      return `${format(fromDate, 'MMM d, yyyy')} - ${format(toDate, 'MMM d, yyyy')}`;
    }
  } catch {
    return 'Invalid date range';
  }
}

interface CalendarMonthProps {
  month: Date;
  selectedRange: { from: Date | null; to: Date | null };
  onDateClick: (date: Date) => void;
  maxDate?: Date;
}

function CalendarMonth({ month, selectedRange, onDateClick, maxDate }: CalendarMonthProps) {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday = 0
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const today = new Date();
  const max = maxDate || today;
  
  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  
  const isDateInRange = (date: Date): boolean => {
    if (!selectedRange.from || !selectedRange.to) return false;
    return isWithinInterval(date, { start: selectedRange.from, end: selectedRange.to });
  };
  
  const isDateStart = (date: Date): boolean => {
    if (!selectedRange.from) return false;
    return isSameDay(date, selectedRange.from);
  };
  
  const isDateEnd = (date: Date): boolean => {
    if (!selectedRange.to) return false;
    return isSameDay(date, selectedRange.to);
  };
  
  const isDateDisabled = (date: Date): boolean => {
    return date > max;
  };
  
  return (
    <div className="flex flex-col">
      <div className="text-center mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {format(month, 'MMMM yyyy')}
        </h3>
      </div>
      
      {/* Week day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekDays.map((day, index) => (
          <div
            key={index}
            className="text-xs font-medium text-gray-500 dark:text-gray-400 text-center py-1"
          >
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, dayIdx) => {
          const isCurrentMonth = isSameMonth(day, month);
          const isToday = isSameDay(day, today);
          const isStart = isDateStart(day);
          const isEnd = isDateEnd(day);
          const inRange = isDateInRange(day);
          const isDisabled = isDateDisabled(day);
          
          return (
            <button
              key={dayIdx}
              type="button"
              onClick={() => !isDisabled && onDateClick(day)}
              disabled={isDisabled}
              className={clsx(
                'relative h-8 w-8 text-xs rounded-md transition-colors',
                !isCurrentMonth && 'text-gray-300 dark:text-gray-600',
                isCurrentMonth && !isDisabled && 'text-gray-900 dark:text-gray-100',
                isDisabled && 'text-gray-300 dark:text-gray-600 cursor-not-allowed',
                isToday && !isStart && !isEnd && 'font-semibold',
                // Range highlighting - background for in-range dates
                inRange && !isStart && !isEnd && 'bg-primary-50 dark:bg-primary-900/20',
                // Start date - solid blue background with proper rounding
                isStart && !isEnd && 'bg-primary-600 text-white font-semibold',
                // End date - solid blue background with proper rounding
                isEnd && !isStart && 'bg-primary-600 text-white font-semibold',
                // Single date selection (start and end are same)
                isStart && isEnd && 'bg-primary-600 text-white rounded-md font-semibold',
                // Start date when there's an end (round left side)
                isStart && !isEnd && inRange && 'rounded-l-md',
                // End date when there's a start (round right side)
                isEnd && !isStart && inRange && 'rounded-r-md',
                // Hover states
                !isDisabled && isCurrentMonth && !isStart && !isEnd && !inRange && 'hover:bg-gray-100 dark:hover:bg-gray-700',
                // Today indicator (only if not selected)
                isToday && !isStart && !isEnd && !inRange && 'ring-2 ring-primary-400'
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TransactionDatePicker({ value, onChange, className, maxDate }: TransactionDatePickerProps) {
  // Check if date range is empty (no filter applied)
  const isEmpty = React.useMemo(() => {
    return !value || !value.from || !value.to || value.from === '' || value.to === '';
  }, [value]);

  // Use the value as-is, don't default to last month when empty
  const validValue = React.useMemo(() => {
    if (isEmpty) {
      return { from: '', to: '' };
    }
    return value;
  }, [value, isEmpty]);

  const [isOpen, setIsOpen] = React.useState(false);
  const [calendarMonth, setCalendarMonth] = React.useState<Date>(() => {
    // Start calendar at the "from" date, or current month
    if (validValue.from) {
      return startOfMonth(parseISO(validValue.from));
    }
    return startOfMonth(subMonths(maxDate || new Date(), 1));
  });
  
  // Track selection state
  const [selectionState, setSelectionState] = React.useState<{
    from: Date | null;
    to: Date | null;
    selecting: 'from' | 'to';
  }>(() => {
    if (!isEmpty && validValue.from && validValue.to) {
      return {
        from: parseISO(validValue.from),
        to: parseISO(validValue.to),
        selecting: 'to',
      };
    }
    return { from: null, to: null, selecting: 'from' };
  });

  // Update selection state when value changes externally
  React.useEffect(() => {
    if (!isEmpty && validValue.from && validValue.to) {
      setSelectionState({
        from: parseISO(validValue.from),
        to: parseISO(validValue.to),
        selecting: 'to',
      });
    } else {
      setSelectionState({ from: null, to: null, selecting: 'from' });
    }
  }, [validValue, isEmpty]);

  // Update calendar month when popup opens to show the selected month or current month
  React.useEffect(() => {
    if (isOpen) {
      if (!isEmpty && validValue.from) {
        setCalendarMonth(startOfMonth(parseISO(validValue.from)));
      } else {
        // Default to current month when no date is selected
        setCalendarMonth(startOfMonth(maxDate || new Date()));
      }
    }
  }, [isOpen, validValue.from, isEmpty, maxDate]);

  const handleDateClick = (date: Date) => {
    if (selectionState.selecting === 'from' || !selectionState.from) {
      // Starting new selection
      setSelectionState({
        from: date,
        to: null,
        selecting: 'to',
      });
    } else {
      // Completing selection
      if (date < selectionState.from) {
        // If clicked date is before from, make it the new from
        setSelectionState({
          from: date,
          to: selectionState.from,
          selecting: 'to',
        });
        onChange({
          from: formatDateIso(date),
          to: formatDateIso(selectionState.from),
        });
      } else {
        // Normal case: set to date
        setSelectionState({
          from: selectionState.from,
          to: date,
          selecting: 'to',
        });
        onChange({
          from: formatDateIso(selectionState.from),
          to: formatDateIso(date),
        });
      }
    }
  };

  const handlePresetClick = (preset: DateRangePreset) => {
    const range = getPresetRange(preset, maxDate);
    onChange(range);
    setIsOpen(false);
  };

  const handlePreviousMonth = () => {
    setCalendarMonth(subMonths(calendarMonth, 1));
  };

  const handleNextMonth = () => {
    const nextMonth = addMonths(calendarMonth, 1);
    const max = maxDate || new Date();
    // Don't allow navigating beyond max date
    if (nextMonth <= max) {
      setCalendarMonth(nextMonth);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Clear the date range (set to empty strings)
    onChange({
      from: '',
      to: '',
    });
  };

  const month1 = calendarMonth;
  const month2 = addMonths(calendarMonth, 1);
  const max = maxDate || new Date();
  const canNavigateNext = addMonths(month2, 1) <= max;

  const displayText = React.useMemo(() => {
    if (isEmpty) {
      return 'All dates';
    }
    return formatDateRangeDisplay(validValue.from, validValue.to);
  }, [isEmpty, validValue]);

  return (
    <div className={clsx('relative', className)}>
      <div className="flex items-center space-x-2 px-3 py-2 h-11 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 flex-1 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors rounded"
          aria-label="Select date range"
          aria-expanded={isOpen}
        >
          <Calendar className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span className="whitespace-nowrap">
            {displayText}
          </span>
        </button>
        {!isEmpty && (
          <button
            type="button"
            onClick={handleClear}
            className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors -mr-1"
            aria-label="Clear date range"
          >
            <X className="h-3 w-3 text-gray-500 dark:text-gray-400" />
          </button>
        )}
      </div>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute left-0 mt-2 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden w-[700px] max-w-[calc(100vw-2rem)]">
            <div className="relative p-6">
              {/* Close Button */}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400"
                aria-label="Close calendar"
              >
                <X className="h-4 w-4" />
              </button>
              
              {/* Preset Options */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3 text-left">
                  Quick Select
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {DATE_RANGE_PRESET_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handlePresetClick(option.value)}
                      className="text-left px-3 py-2 text-sm rounded-md transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 whitespace-nowrap"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calendar Navigation */}
              <div className="flex items-center justify-center gap-4 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={handlePreviousMonth}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[180px] text-center">
                  {format(month1, 'MMM yyyy')} - {format(month2, 'MMM yyyy')}
                </span>
                <button
                  onClick={handleNextMonth}
                  disabled={!canNavigateNext}
                  className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              {/* Two-Month Calendar */}
              <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 justify-center">
                <CalendarMonth
                  month={month1}
                  selectedRange={{
                    from: selectionState.from,
                    to: selectionState.to,
                  }}
                  onDateClick={handleDateClick}
                  maxDate={max}
                />
                <CalendarMonth
                  month={month2}
                  selectedRange={{
                    from: selectionState.from,
                    to: selectionState.to,
                  }}
                  onDateClick={handleDateClick}
                  maxDate={max}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

